import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Building2, Check, Copy, Plug, Unplug } from 'lucide-react';
import { api } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDateTime } from '@/lib/dates';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, Spinner } from '@/features/common/ui';

interface ConnectionRow {
  id: string;
  clientName: string;
  scopes: string[];
  /**
   * The books this one grant reaches, fixed when it was approved. One
   * grant, several orgs, one revoke: disconnecting cuts it off from all of
   * them at once, which is what the person who lent out their identity
   * expects "disconnect" to mean.
   */
  orgs: Array<{ id: string; name: string }>;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  // Carried by the API today and read if present; not part of the contract
  // the card depends on, so their absence renders a shorter line, not a crash.
  redirectHost?: string;
  userName?: string | null;
}

/** Live until revoked. Derived rather than trusted from a flag, so the two cannot disagree. */
const isActive = (row: ConnectionRow) => row.revokedAt === null;

/**
 * "Connected assistants" - the OAuth grants a person made themselves.
 *
 * ── WHY THIS IS NOT A ROW IN THE API KEYS TABLE ──
 * The two look alike - a name, a last-used stamp, a revoke button - and are
 * different objects in the one way that decides where the button lives:
 *
 *   1. Who may create one. `apiKey.manage` is admin-only. A connection is
 *      created by any member with a session, by consenting. Requiring
 *      `apiKey.manage` to connect Claude would make the feature admin-only,
 *      which defeats it.
 *   2. Who may revoke one. Revoking a key needs `apiKey.manage`. A user must
 *      ALWAYS be able to disconnect their own assistant without an admin - they
 *      are the one who lent out their own identity, and "ask your admin to
 *      disconnect ChatGPT from your account" is not an acceptable answer.
 *   3. Different columns. A connection has a client, a destination host, a
 *      scope set and a chosen set of books; a key has a prefix and a
 *      creator.
 *
 * If the two lived in one table under one permission gate, rule 1 or rule 2
 * would have to break. So: two cards, two tables, one settings page.
 */
export function ConnectionsCard() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const canSeeEveryone = perms.can('apiKey', 'view');
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(false);

  const scope = showAll && canSeeEveryone ? 'org' : 'mine';
  const connections = useQuery({
    queryKey: ['oauth-connections', org?.id, scope],
    queryFn: () => api.get<{ items: ConnectionRow[] }>(`/oauth/connections${scope === 'org' ? '?scope=org' : ''}`),
    enabled: !!org?.id,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/oauth/connections/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['oauth-connections', org?.id] });
    },
  });

  // The URL a person pastes into Claude or ChatGPT. Built from the browser's
  // own origin rather than a configured value, because that is by definition
  // the host they reached Fint on - and it is the string the protected-resource
  // metadata has to match character for character.
  const serverUrl = `${window.location.origin}/v1/mcp`;

  const items = connections.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Bot className="size-4 text-muted-foreground" aria-hidden />
          Connected assistants
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Connect an AI agent - Claude, ChatGPT or any MCP client - to your books and ask about them in English. You approve the
          connection yourself, tick which books it can see, and can disconnect it here at any
          time — no administrator needed.
        </p>

        <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
          <p className="text-sm font-medium">Setting one up</p>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>
              In Claude, go to Settings → Connectors → Add custom connector. In ChatGPT, use
              Settings → Connectors.
            </li>
            <li>Paste this address, exactly as written:</li>
          </ol>
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded bg-background px-2 py-1 font-mono text-xs">
              {serverUrl}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void navigator.clipboard?.writeText(serverUrl);
                setCopied(true);
              }}
            >
              {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {/*
              The trailing slash matters and nobody would guess it: Claude
              compares this string byte for byte against the `resource` field in
              Fint's protected-resource metadata, so an extra character means a
              connector that fails with a generic error.
            */}
            No trailing slash. Claude matches this address exactly against what Fint publishes, so an
            extra character stops it connecting. Fint will then ask you to sign in and show you what
            the assistant is asking for.
          </p>
        </div>

        {canSeeEveryone ? (
          <div className="flex items-start gap-2 text-sm">
            <input
              id="connections-show-all"
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              aria-describedby="connections-show-all-hint"
              className="mt-0.5"
            />
            <div>
              <label htmlFor="connections-show-all">Show everybody’s connections</label>
              <p id="connections-show-all-hint" className="text-xs text-muted-foreground">
                Every assistant connected to {org?.name ?? 'this book'}, by anyone. Disconnecting
                someone else’s is the same act as revoking their API key.
              </p>
            </div>
          </div>
        ) : null}

        {connections.isError ? (
          <ErrorState error={connections.error} onRetry={() => connections.refetch()} />
        ) : connections.isLoading ? (
          <Spinner />
        ) : items.length ? (
          <ul className="space-y-2">
            {items.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3"
              >
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-medium">{row.clientName}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.redirectHost ? `Sends to ${row.redirectHost} · ` : ''}
                    {describeScopes(row.scopes)}
                  </p>
                  {/*
                    One chip per book, always the names: a connection
                    that reaches three books and says "3 books" makes a
                    person open each grant to find the one that can see the
                    client they are worried about.
                  */}
                  <ul className="flex flex-wrap items-center gap-1" aria-label="Books this connection can see">
                    {row.orgs.map((org) => (
                      <li key={org.id}>
                        <Badge variant="outline">
                          <Building2 className="size-3 text-muted-foreground" aria-hidden />
                          {org.name}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">
                    {showAll ? `${row.userName ?? 'Someone'} · ` : ''}
                    Connected {formatDateTime(row.createdAt)} ·{' '}
                    {row.lastUsedAt ? `last used ${formatDateTime(row.lastUsedAt)}` : 'never used'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={isActive(row) ? 'success' : 'neutral'}>
                    {isActive(row) ? 'Connected' : 'Disconnected'}
                  </Badge>
                  {isActive(row) ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={revoke.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Disconnect "${row.clientName}" from ${describeOrgs(row.orgs)}? It stops working immediately, on its very next request.`,
                          )
                        ) {
                          revoke.mutate(row.id);
                        }
                      }}
                    >
                      <Unplug className="size-4" aria-hidden />
                      Disconnect
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={Plug}
            title="No assistants connected"
            description="Paste the address above into your AI agent's connector settings to connect one."
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Scopes in words.
 *
 * The raw strings are protocol, not language: nobody reading a settings page
 * should have to know what `fint:write` means to answer "can this thing change
 * my books". Neither answer says "can write", because neither can - the wider
 * one proposes, and a person still confirms in Fint.
 */
function describeScopes(scopes: string[]): string {
  return scopes.includes('fint:write')
    ? 'reads, and can raise queries, attach files and draft'
    : 'reads only';
}

/**
 * The books a grant reaches, for the confirm prompt - so "disconnect"
 * says what it is disconnecting from before it does.
 */
function describeOrgs(orgs: ConnectionRow['orgs']): string {
  if (orgs.length === 0) return 'your books';
  if (orgs.length === 1) return orgs[0].name;
  return `${orgs.length} books`;
}
